import { ApiError } from '@signalvote/api-client';
import { Download } from 'lucide-react';
import { PendingButton } from '@/components/ui';
import { useVoterListDownload } from '@/hooks/queries';
import { PUBLIC_LINK_HEADLINE, PublicLinkPage } from './PublicLinkPage';

function problem(error: Error): string {
  if (error instanceof ApiError && error.status === 404) {
    return 'This link doesn’t match a voter list. Check that it matches the one in your email.';
  }
  if (error instanceof ApiError && error.status === 410) return error.message;
  return 'We couldn’t start the download. Try again.';
}

/**
 * A delivered voter list's emailed link: open to whoever holds it, no sign-in. Each press signs a fresh
 * download of the list's zip, so the link in the email never goes stale.
 */
export function VoterListDownloadPage({ token }: { token: string }) {
  const { mutate, isPending, error } = useVoterListDownload(token);
  const download = () => mutate(undefined, { onSuccess: (link) => window.location.assign(link.url) });

  return (
    <PublicLinkPage>
      <h1 className={PUBLIC_LINK_HEADLINE}>Your voter list</h1>
      <p className="mt-3 text-[1.0625rem] text-muted-foreground">
        It downloads as a zip file. Open it to get a spreadsheet with a row for every voter.
      </p>
      <PendingButton className="mt-8" pending={isPending} onClick={download}>
        <Download className="size-4" aria-hidden />
        Download your voter list
      </PendingButton>
      {error && (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {problem(error)}
        </p>
      )}
    </PublicLinkPage>
  );
}
